import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { DialogComponent, DialogModule } from '@syncfusion/ej2-angular-popups';
import { HtmlEditorService, ImageService, ImageSettingsModel, LinkService, RichTextEditor, ToolbarService, RichTextEditorModule, QuickToolbarService } from '@syncfusion/ej2-angular-richtexteditor';
import { ExportInterfaceActivationEmailDTO } from 'src/app/base/models/models.swagger.generated';
import { TranslateModule } from '@ngx-translate/core';

import { AppErrorMessagesComponent } from '../../../../../../base/error-messages/app-error-messages.component';

@Component({
    selector: 'app-activation-email-preview',
    templateUrl: './activation-email-preview.component.html',
    styleUrls: ['./activation-email-preview.component.scss'],
    providers: [ToolbarService, LinkService, ImageService, HtmlEditorService, QuickToolbarService],
    imports: [DialogModule, ReactiveFormsModule, AppErrorMessagesComponent, RichTextEditorModule, TranslateModule]
})
export class ActivationEmailPreviewComponent implements OnInit {

  @Input()
  public set generatedActivationEmails(activationEmails: ExportInterfaceActivationEmailDTO[]) {
    this.activationEmails = activationEmails;
    if (this.activationEmails?.length > 0) {
      this.currentActivationEmail = this.activationEmails[0];
      this.currentActivationEmailIndex = 0;
      this.initialiazeActivationEmailForm();
    }
  }

  @Input()
  public set showPreview(value: boolean) {
    if (value === true) {
      this.dialog?.show();
    }
    else {
      this.dialog?.hide();
    }
  }

  @Input()
  public sendAllowed: boolean;

  @Output() sendEmailClicked = new EventEmitter<ExportInterfaceActivationEmailDTO>();
  @Output() previewClosed = new EventEmitter<any>();
  @Output() sendAllEmailClicked = new EventEmitter<ExportInterfaceActivationEmailDTO[]>();

  @ViewChild('emailPreviewDialog')
  public dialog: DialogComponent;

  @ViewChild('emailContent')
  public emailContent: RichTextEditor;

  public insertImageSettings: ImageSettingsModel = { saveFormat: 'Base64' };

  public activationEmails: ExportInterfaceActivationEmailDTO[];
  private currentActivationEmail: ExportInterfaceActivationEmailDTO;
  public currentActivationEmailIndex: number;
  public activationEmailForm: UntypedFormGroup;
  public renderRTE: boolean = false;
  public tools: object;

  constructor(
    private fb: UntypedFormBuilder
  ) { }

  ngOnInit(): void {
    this.tools = {
      items: ['Bold', 'Italic', 'Underline', 'StrikeThrough', 'ClearFormat', '|', 'FontSize', 'FontColor', 'BackgroundColor', 'Formats', 'Alignments', 'NumberFormatList',
        'BulletFormatList', '|', 'CreateLink', 'Image', '|', 'SourceCode', '|', 'Undo', 'Redo'
      ]
    }

    this.initialiazeActivationEmailForm();
  }

  onActionComplete(args: any): void {
    //Fix for closing source code view
    if (args.requestType === 'Preview') {
      const sourceViewElement = document.getElementById('emailContent_source-view');
      if (sourceViewElement) {
        sourceViewElement.remove();
      }
    }
  }

  private initialiazeActivationEmailForm(): void {
    this.activationEmailForm = this.fb.group({
      configurationId: [null, Validators.required],
      templateId: [null],
      subject: [null, Validators.required],
      emailContent: [null, Validators.required],
      emailToUserId: [null, Validators.required],
      emailTo: [{ value: null, disabled: true }, Validators.required]
    })
    this.loadCurrentActivationEmailToForm();
  }

  private loadCurrentActivationEmailToForm(): void {
    if (this.currentActivationEmail) {
      this.activationEmailForm.setValue({
        configurationId: this.currentActivationEmail.configurationId,
        templateId: this.currentActivationEmail.templateId,
        subject: this.currentActivationEmail.subject,
        emailContent: this.currentActivationEmail.emailContent,
        emailToUserId: this.currentActivationEmail.emailToUserId,
        emailTo: this.currentActivationEmail.emailTo
      });
    }
  }

  public sendClicked(): void {
    var activationEmailDTO = this.GetActivationEmailDTOFromForm();
    this.sendEmailClicked.emit(activationEmailDTO);
  }

  private GetActivationEmailDTOFromForm(): ExportInterfaceActivationEmailDTO {
    var formRawValue = this.activationEmailForm.getRawValue();
    var activationEmailDTO: ExportInterfaceActivationEmailDTO = new ExportInterfaceActivationEmailDTO({
      configurationId: formRawValue.configurationId,
      templateId: formRawValue.templateId,
      emailContent: formRawValue.emailContent,
      emailTo: formRawValue.emailTo,
      emailToUserId: formRawValue.emailToUserId,
      subject: formRawValue.subject
    });
    return activationEmailDTO;
  }

  public sendAllClicked(): void {
    // First save the current form changes to the current activation email dto
    // First save the current changes in the form to current activation email dto
    var activationEmailDTO = this.GetActivationEmailDTOFromForm();
    this.activationEmails[this.currentActivationEmailIndex] = activationEmailDTO;
    this.sendAllEmailClicked.emit(this.activationEmails);
  }

  public onOpen(args: any): void {
    args.preventFocus = true;
    this.renderRTE = true;
  }

  public previousEmailClicked(): void {
    if (this.activationEmails?.length > 1 && this.currentActivationEmailIndex > 0) {
      // First save the current changes in the form to current activation email dto
      var activationEmailDTO = this.GetActivationEmailDTOFromForm();
      this.activationEmails[this.currentActivationEmailIndex] = activationEmailDTO;
      // change the current activation email to previous one
      this.currentActivationEmailIndex--;
      this.currentActivationEmail = this.activationEmails[this.currentActivationEmailIndex];
      this.loadCurrentActivationEmailToForm();
    }
  }

  public nextEmailClicked(): void {
    if (this.activationEmails?.length > 1 && this.currentActivationEmailIndex < this.activationEmails.length - 1) {
      // First save the current changes in the form to current activation email dto
      var activationEmailDTO = this.GetActivationEmailDTOFromForm();
      this.activationEmails[this.currentActivationEmailIndex] = activationEmailDTO;
      // change the current activation email to next one
      this.currentActivationEmailIndex++;
      this.currentActivationEmail = this.activationEmails[this.currentActivationEmailIndex];
      this.loadCurrentActivationEmailToForm();
    }
  }

  public cancelClicked(): void {
    this.dialog?.hide();
    this.renderRTE = false;
    this.previewClosed.emit();
  }

}
